import React, { useLayoutEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { alpha, CircularProgress, IconButton, Tooltip, Typography } from '@material-ui/core';
import { AddCircleOutlineOutlined } from '@material-ui/icons';
import { isTraceBoardItemEqual } from './utils/isTraceBoardItemEqual';
import { useTraceBoardContext } from './TraceBoardContext';
const StyledDiv = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 10px;
  min-height: min-content;
  border: 0px solid;
  &:last-child {
    margin-bottom: 0;
    &.selected {
      margin-bottom: -3px;
    }
  }
  &.selected {
    margin: -3px -3px 7px -3px;
    border: 3px solid ${props => alpha(props.theme.palette.primary.main, 0.8)};
    &.deleted {
      border: 3px solid ${p => p.theme.palette.error.main};
      border-left: solid 4px ${p => p.theme.palette.error.main};
      margin-left: -4px;
    }
  }
  &.collapsed {
    .title-container {
      padding-bottom: 0;
      .title {
        padding-right: 0;
      }
    }
    .content {
      align-items: center;
      flex-direction: row;
      white-space: nowrap;
      overflow: hidden;
      .description-box {
        overflow: hidden;
        .description {
          padding-left: 8px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }
  &.deleted {
    border-left: solid 4px ${p => p.theme.palette.error.main};
    margin-left: -4px;
  }
  &:hover .hover-only {
    opacity: 1;
    max-height: 100px;
  }
  .hover-only {
    opacity: 0;
    max-height: 0;
    overflow: hidden;
    transition: opacity 300ms, max-height 300ms;
    transition-timing-function: cubic-bezier(0.4, 0.82, 0.165, 1);
  }
  .content {
    display: flex;
    flex-direction: column;
    padding: 12px;
    margin: 0;
  }
  .title-container {
    display: flex;
    align-items: center;
    padding-bottom: 6px;
    white-space: pre-wrap;
  }
  .title-icon {
    padding-right: 6px;
  }
  .title {
    flex: 1;
    font-weight: bold;
    padding-right: 12px;
  }
  .title-accessory {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    align-self: flex-start;
    overflow: hidden;
  }
  .description-box {
    .description {
      position: relative;
      z-index: 0;
      white-space: pre-wrap;
      line-height: 1.43;
      &:hover::before {
        content: '';
        display: block;
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        background: #f7f8fc;
        border: 1px solid #f4f4f4;
        border-radius: 4px;
        z-index: -1;
      }
    }
  }
  .link-new-button {
    position: absolute;
    right: -17px;
    top: 50%;
    transform: translateY(-19px);
    .MuiIconButton-label {
      background: #ecf5ff;
      border-radius: 24px;
    }
    svg {
      color: #2b98f0;
    }
  }
  .card-loading {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.75);
    border-radius: 4px;
  }
`;
const TraceBoardCard = props => {
    var _a, _b, _c, _d;
    const { item, column, rowIndex, style: styleProp, onSize } = props;
    const elementRef = useRef(null);
    const { ref, entry, inView } = useInView({
        threshold: 0,
        rootMargin: '100px'
    });
    const { columns, collapse, elements, selected, isItemLoading, setSelected } = useTraceBoardContext();
    const nextColumn = columns.find((_, i) => { var _a; return ((_a = columns[i - 1]) === null || _a === void 0 ? void 0 : _a.id) === item.columnId; });
    const isNew = item.id === 'new';
    const isLoading = isItemLoading(item);
    const isSelected = !!selected && isTraceBoardItemEqual(selected, item);
    const isCollapsed = !isSelected && collapse.isCollapsed(rowIndex);
    const className = ['board-card'];
    if (isCollapsed)
        className.push('collapsed');
    if (isSelected)
        className.push('selected');
    if (item.deleted)
        className.push('deleted');
    // save minHeight so that hiding the content
    // does not affect container height and the scrollbar.
    // override saved styling from parent if entries exist
    const style = Object.assign({}, styleProp);
    if (entry) {
        const height = entry.boundingClientRect.height;
        onSize === null || onSize === void 0 ? void 0 : onSize(height);
        style.minHeight = height;
    }
    if (inView) {
        delete style.minHeight;
    }
    const renderOptions = {
        rowIndex,
        item,
        column,
        isSelected,
        isCollapsed,
        elementRef,
        cardStyle: style
    };
    useLayoutEffect(() => {
        const element = elementRef.current;
        if (element && inView) {
            elements.register(rowIndex, item, element);
        }
        // update to either hide or show connectors based on inView value
        elements.publish(item.columnId, rowIndex);
        return () => {
            element && elements.unregister(rowIndex, item);
        };
    }, [elements, inView, item, rowIndex]);
    const card = (_a = column.renderCard) === null || _a === void 0 ? void 0 : _a.call(column, renderOptions);
    if (card) {
        return card;
    }
    const titleIcon = (_b = column.renderTitleIcon) === null || _b === void 0 ? void 0 : _b.call(column, renderOptions);
    const titleAccessory = (_c = column.renderTitleAccessory) === null || _c === void 0 ? void 0 : _c.call(column, renderOptions);
    return (React.createElement(StyledDiv, { style: style, className: className.join(' '), ref: element => {
            ref(element);
            elementRef.current = element;
        }, onClick: () => {
            setSelected(selected => {
                return selected &&
                    selected.id === item.id &&
                    selected.columnId === item.columnId
                    ? undefined
                    : { id: item.id, columnId: item.columnId };
            });
        } }, inView && (React.createElement(React.Fragment, null,
        React.createElement("div", { className: 'content' },
            React.createElement("div", { className: 'title-container' },
                titleIcon && React.createElement("div", { className: 'title-icon' }, titleIcon),
                React.createElement(Typography, { variant: 'body1', className: 'title' }, item.title),
                titleAccessory && (React.createElement("div", { className: 'title-accessory' }, titleAccessory))),
            React.createElement("div", { className: 'description-box', onClick: event => {
                    event.stopPropagation();
                    setSelected({ id: item.id, columnId: item.columnId });
                } },
                React.createElement(Typography, { variant: 'body2', className: 'description', color: item.description ? 'initial' : 'textSecondary' }, item.description))),
        !isNew && ((_d = column.renderSubContent) === null || _d === void 0 ? void 0 : _d.call(column, renderOptions)),
        isSelected && nextColumn && item.id !== 'new' && (React.createElement(Tooltip, { arrow: true, placement: 'right', title: `Link New ${nextColumn.name}` },
            React.createElement(IconButton, { size: 'small', className: 'link-new-button', onClick: event => event.stopPropagation() },
                React.createElement(AddCircleOutlineOutlined, null)))),
        isLoading && (React.createElement("div", { className: 'card-loading' },
            React.createElement(CircularProgress, { size: 24, color: 'primary' })))))));
};
export default TraceBoardCard;
//# sourceMappingURL=TraceBoardCard.js.map